import { memo } from 'react'
import * as React from 'react'
import { lazily } from 'react-lazily'
import { Redirect, RouteComponentProps, Switch } from 'react-router-dom'
import { CompatRoute } from 'react-router-dom-v5-compat'
import { EMPLOYMENT_ROUTES } from 'shared/components/Onboarding/EmploymentHistory/EmploymentHistory.constants'
import { InboxRedirect } from 'shared/components/WorkIssues/InboxRedirect'

import { ApprovedRoute } from 'components/ApprovedRoute'
import { PrivateRoute } from 'components/PrivateRoute'
import { DeadEndDeactivated } from 'pages/Registration/SignUp/DeadEndDeactivated'
import { DriverAssignments } from './pages/Company/DriverAssignments'
import { MyPerformance } from './pages/Company/MyPerformance'

const { DriverScreeningConsent } = lazily(() => import('./pages/Company/DriverScreeningConsent'))
const { Management } = lazily(() => import('./pages/Management/Management'))
const { PasswordlessRedirect } = lazily(() => import('./pages/SignIn/PasswordlessRedirect'))
const { FleetInbox, FleetIssues } = lazily(() => import('./pages/Fleet/FleetIssues'))
const { ChatsPage } = lazily(() => import('./pages/Chats/ChatsPage'))
const { FleetMembers, FleetEquipment } = lazily(() => import('./pages/Fleet/FleetDirectory'))

const { PermissionsSettings } = lazily(() => import('./pages/PermissionsSettings/PermissionsSettings'))
const { ConfirmFailedPage } = lazily(() => import('pages/ConfirmFailed/ConfirmFailed'))
const { ConfirmInvite } = lazily(() => import('pages/ConfirmInvite/ConfirmInvite'))
const { PageNotFound } = lazily(() => import('pages/ErrorPage/PageNotFound'))
const { HomePage } = lazily(() => import('pages/Home/Home'))
const { JobDetails } = lazily(() => import('pages/JobDetails/JobDetails'))
const { BookmarkedLoads } = lazily(() => import('pages/Jobs/BookmarkedLoads'))
const { JobsPage } = lazily(() => import('pages/Jobs/JobsPage'))
const { JobCopy } = lazily(() => import('pages/Jobs/JobCopy'))
const { JobsTemplates } = lazily(() => import('pages/Jobs/JobsTemplates'))
const { JobsNewTemplate } = lazily(() => import('pages/Jobs/JobsNewTemplate'))
const { JobsEditTemplate } = lazily(() => import('pages/Jobs/JobsEditTemplate'))
const { SelfServeBrokerLoadTrackingPage } = lazily(() => import('pages/JobTracking/SelfServeBrokerLoadTracking'))
const { SelfServeBrokerLoadTrackingLookupPage } = lazily(() => import('pages/JobTracking/SelfServeBrokerLoadTrackingLookupPage'))
const { LoadDetails } = lazily(() => import('pages/LoadDetails/LoadDetails'))
const { OnboardingDriverApplication } = lazily(() => import('pages/Onboarding/OnboardingDriverApplication'))
const { OnboardingEmploymentHistory } = lazily(() => import('pages/Onboarding/OnboardingEmploymentHistory/OnboardingEmploymentHistory'))
const { RegistrationPage } = lazily(() => import('pages/Registration/Registration'))
const { SearchFormAsync } = lazily(() => import('pages/SearchForm/SearchFormAsync'))
const { SearchResultsFormAsync } = lazily(() => import('pages/SearchForm/SearchResultsFormAsync'))
const { UploadCDLPage } = lazily(() => import('pages/UploadCDLPage/UploadCDLPage'))
const { AccountActivity } = lazily(() => import('./pages/CTCash/AccountActivity'))

const { AddCardHolder } = lazily(() => import('./pages/CTCash/AddCardHolder'))
const { CardManagement } = lazily(() => import('./pages/CTCash/CardManagement'))
const { ChangeWeeklyLimit } = lazily(() => import('./pages/CTCash/ChangeWeeklyLimit'))
const { InboxRateconBook } = lazily(() => import('./pages/ExternalBook/InboxRateconBook'))
const { ExternalBook } = lazily(() => import('./pages/ExternalBook/ExternalBook'))
const { JobDetailsEdit } = lazily(() => import('./pages/JobDetails/JobDetailsEdit'))
const { JobCreateInvoice } = lazily(() => import('./pages/JobInvoice/JobCreateInvoice'))
const { JobSendInvoiceToBroker } = lazily(() => import('./pages/JobInvoice/JobSendInvoiceToBroker'))
const { JobSendInvoiceToFactoringCompany } = lazily(() => import('./pages/JobInvoice/JobSendInvoiceToFactoringCompany'))
const { JobTracking } = lazily(() => import('./pages/JobTracking/JobTracking'))
const { ContactedLoads } = lazily(() => import('./pages/Jobs/ContactedLoads'))
const { JobsPastPage } = lazily(() => import('./pages/Jobs/JobsPastPage'))
const { LoadDetailsBook } = lazily(() => import('./pages/LoadDetails/LoadDetailsBook'))
const { MarketConditions } = lazily(() => import('./pages/MarketConditions/MarketConditions'))
const { MobileAuth } = lazily(() => import('./pages/MobileAuth/MobileAuth'))
const { OnboardingBrokerSetup } = lazily(() => import('./pages/Onboarding/Broker/OnboardingBrokerSetup'))
const { OnboardingELDSetup } = lazily(() => import('./pages/Onboarding/ELD/OnboardingELDSetup'))
const { OnboardingEquipmentAddTrailer } = lazily(() => import('./pages/Onboarding/Equipment/OnboardingEquipmentAddTrailer'))
const { OnboardingEquipmentAddTruck } = lazily(() => import('./pages/Onboarding/Equipment/OnboardingEquipmentAddTruck'))
const { OnboardingEquipmentEditTrailer } = lazily(() => import('./pages/Onboarding/Equipment/OnboardingEquipmentEditTrailer'))
const { OnboardingEquipmentEditTruck } = lazily(() => import('./pages/Onboarding/Equipment/OnboardingEquipmentEditTruck'))
const { OnboardingEquipmentTruckDocumentsPage } = lazily(() => import('./pages/Onboarding/Equipment/OnboardingEquipmentTruckDocuments'))
const { OnboardingAddDriver } = lazily(() => import('./pages/Onboarding/OnboardingAddDriver'))
const { OnboardingAddAdminDriver } = lazily(() => import('./pages/Onboarding/OnboardingAddAdminDriver'))
const { OnboardingAddAdmin } = lazily(() => import('./pages/Onboarding/OnboardingAddAdmin'))
const { OnboardingAddUser } = lazily(() => import('./pages/Onboarding/OnboardingAddUser'))
const { OnboardingBankAccount } = lazily(() => import('./pages/Onboarding/OnboardingBankAccount'))
const { OnboardingStripeVerifyProfile } = lazily(() => import('./pages/Onboarding/OnboardingStripeVerifyProfile'))
const { OnboardingConfirmCTCash } = lazily(() => import('./pages/Onboarding/OnboardingConfirmCTCash'))
const { OnboardingSignLeaseAgreement } = lazily(() => import('./pages/Onboarding/LeaseAgreement/OnboardingSignLeaseAgreement'))
const { OnboardingSignLeaseAgreementAmendment } = lazily(() => import('./pages/Onboarding/LeaseAgreement/OnboardingSignLeaseAgreementAmendment'))
const { OnboardingEmploymentHistoryAddPosition } = lazily(() => import('./pages/Onboarding/OnboardingEmploymentHistory/OnboardingEmploymentHistoryAddPosition'))
const { OnboardingEmploymentHistoryEditPosition } = lazily(() => import('./pages/Onboarding/OnboardingEmploymentHistory/OnboardingEmploymentHistoryEditPosition'))
const { OnboardingFleetApplication } = lazily(() => import('./pages/Onboarding/OnboardingFleetApplication'))
const { OnboardingFleetDetails } = lazily(() => import('./pages/Onboarding/OnboardingFleetDetails'))
const { OnboardingInsuranceSetupPage } = lazily(() => import('./pages/Onboarding/InsuranceSetup/OnboardingInsuranceSetupPage'))
const { OnboardingInsuranceBeneficiaryPage } = lazily(() => import('./pages/Onboarding/InsuranceSetup/OnboardingInsuranceBeneficiaryPage'))
const { OnboardingInsuranceDocuments } = lazily(() => import('./pages/Onboarding/OnboardingInsuranceDocuments'))
const { OnboardingMaintenanceSetAside } = lazily(() => import('./pages/Onboarding/OnboardingMaintenanceSetAside'))
const { OnboardingTaskListPage } = lazily(() => import('./pages/Onboarding/OnboardingTaskList'))
const { OnboardingTruckLeaseApplication } = lazily(() => import('./pages/Onboarding/TruckLeaseApplication/OnboardingTruckLeaseApplication'))

const { OnboardingTaxInfo } = lazily(() => import('./pages/Onboarding/OnboardingTaxInfo'))
const { OnboardingApplicationStatusPage } = lazily(() => import('./pages/Onboarding/OnboardingApplicationStatusPage'))
const { OnboardingDrugScreenPage } = lazily(() => import('./pages/Onboarding/DrugScreen/OnboardingDrugScreenPage'))
const { OnboardingClearinghousePage } = lazily(() => import('./pages/Onboarding/OnboardingClearinghousePage'))
const { OnboardingWelcomePackage } = lazily(() => import('./pages/Onboarding/WelcomePackage/OnboardingWelcomePackage'))
const { OnboardingTruckDecals } = lazily(() => import('./pages/Onboarding/TruckDecals/OnboardingTruckDecals'))
const { OnboardingNextTask } = lazily(() => import('./pages/Onboarding/OnboardingNextTask'))
const { PlaidRedirect } = lazily(() => import('./pages/PlaidRedirect/PlaidRedirect'))
const { BankAccountPage } = lazily(() => import('./pages/ProfilePage/BankAccountPage'))
const { BankAccountAssetsPage } = lazily(() => import('./pages/ProfilePage/BankAccountAssetsPage'))
const { BrokersPage } = lazily(() => import('./pages/ProfilePage/BrokersPage'))
const { ConfirmCTCashPage } = lazily(() => import('./pages/ProfilePage/ConfirmCTCashPage'))
const { EquipmentAddTrailerPage } = lazily(() => import('./pages/ProfilePage/EquipmentAddTrailerPage'))
const { EquipmentAddTruckPage } = lazily(() => import('./pages/ProfilePage/EquipmentAddTruckPage'))
const { EquipmentEditTrailerPage } = lazily(() => import('./pages/ProfilePage/EquipmentEditTrailerPage'))
const { EquipmentEditTruckPage } = lazily(() => import('./pages/ProfilePage/EquipmentEditTruckPage'))
const { EquipmentTrailerPage } = lazily(() => import('./pages/Equipment/EquipmentTrailerPage'))
const { EquipmentTruckPage } = lazily(() => import('./pages/Equipment/EquipmentTruckPage'))
const { EquipmentPage } = lazily(() => import('./pages/ProfilePage/EquipmentPage'))
const { FleetPage } = lazily(() => import('./pages/ProfilePage/FleetPage'))
const { FleetMembersPage } = lazily(() => import('./pages/ProfilePage/FleetMembersPage'))
const { PaymentInformationPage } = lazily(() => import('./pages/ProfilePage/PaymentInformationPage'))
const { VerifyCode } = lazily(() => import('./pages/Registration/VerifyCode/VerifyCode'))
const { RevenueCalculator } = lazily(() => import('./pages/RevenueCalculator/RevenueCalculator'))
const { AddAdmin } = lazily(() => import('./pages/User/AddAdmin'))
const { AddAdminDriver } = lazily(() => import('./pages/User/AddAdminDriver'))
const { AddDriver } = lazily(() => import('./pages/User/AddDriver'))
const { AddUser } = lazily(() => import('./pages/User/AddUser'))
const { DeactivateUser } = lazily(() => import('./pages/User/DeactivateUser'))
const { EditUserRole } = lazily(() => import('./pages/User/EditUserRole'))
const { SignInPage } = lazily(() => import('pages/SignIn/SignIn'))
const { ProfilePage } = lazily(() => import('pages/ProfilePage/ProfilePage'))
const { SafetyDashboard } = lazily(() => import('pages/Safety/SafetyDashboard'))
const { CancelSubscription } = lazily(() => import('pages/CancelAccount/CancelSubscription'))
const { SubscriptionPage } = lazily(() => import('pages/ProfilePage/SubscriptionPage'))
const { ContractorPayments } = lazily(() => import('pages/ContractorPayments/ContractorPayments'))
const { ContractorPaymentsPayrollBankAccount } = lazily(() => import('pages/ContractorPayments/ContractorPaymentsPayrollBankAccount'))
const { ContractorPaymentsAddContractors } = lazily(() => import('pages/ContractorPayments/ContractorPaymentsAddContractors'))
const { ContractorPaymentsDirectDeposit } = lazily(() => import('pages/ContractorPayments/ContractorPaymentsDirectDeposit'))
const { ContractorPaymentsDebitCard } = lazily(() => import('pages/ContractorPayments/ContractorPaymentsDebitCard'))
const { ContractorPaymentsContractors } = lazily(() => import('pages/ContractorPayments/ContractorPaymentsContractors'))
const { ContractorPaymentsPay } = lazily(() => import('pages/ContractorPayments/ContractorPaymentsPay'))
const { ContractorPaymentsPersonalInfo } = lazily(() => import('pages/ContractorPayments/ContractorPaymentsPersonalInfo'))
const { ContractorPaymentsPersonalSettings } = lazily(() => import('pages/ContractorPayments/ContractorPaymentsPersonalSettings'))
const { ContractorPaymentsInstantPayoutsOnboarding } = lazily(() => import('pages/ContractorPayments/ContractorPaymentsInstantPayoutsOnboarding'))
const { ContractorPaymentsVerifyProfile } = lazily(() => import('pages/ContractorPayments/ContractorPaymentsVerifyProfile'))
const { Report } = lazily(() => import('pages/Report/Report'))
const { OnboardingPspConsent } = lazily(() => import('pages/Onboarding/PspConsent/OnboardingPspConsent'))
const { OnboardingDacConsent } = lazily(() => import('pages/Onboarding/DacConsent/OnboardingDacConsent'))

const { Deductions } = lazily(() => import('pages/Vendor/Deductions/Deductions'))
const { DeductionDetail } = lazily(() => import('pages/Vendor/Deductions/DeductionDetail'))
const { DeductionRefinance } = lazily(() => import('pages/Vendor/Deductions/DeductionRefinance'))
const { OnboardingPickUpPayAmendment } = lazily(() => import('pages/Onboarding/PickUpPayAmendment'))
const { VendorDrivers } = lazily(() => import('pages/Vendor/Drivers/VendorDrivers'))
const { VendorDriverDetail } = lazily(() => import('pages/Vendor/Drivers/VendorDriverDetail'))
const { VendorDriverDetailAssignTruck } = lazily(() => import('pages/Vendor/Drivers/VendorDriverDetailAssignTruck'))
const { VendorDriverDetailGenerateLease } = lazily(() => import('pages/Vendor/Drivers/VendorDriverDetailGenerateLease'))
const { VendorTrucks } = lazily(() => import('pages/Vendor/Trucks/VendorTrucks'))
const { VendorTruckDetail } = lazily(() => import('pages/Vendor/Trucks/VendorTruckDetail'))
const { VendorTruckAdd } = lazily(() => import('pages/Vendor/Trucks/VendorTruckAdd'))
const { VendorTruckImport } = lazily(() => import('pages/Vendor/Trucks/VendorTruckImport'))
const { VendorTruckEdit } = lazily(() => import('pages/Vendor/Trucks/VendorTruckEdit'))

export const INDEX_PATH = '/'
export const SIGN_IN_PATH = '/signin/'
export const VERIFY_PATH = '/verify/'
export const SELF_SERVE_BROKER_LOAD_TRACKING_LOOKUP_PATH = '/job-tracking/'
export const SELF_SERVE_BROKER_LOAD_TRACKING_PATH = '/job-tracking/:brokerLoadId'
export const REGISTRATION_PATH = '/registration/:bundle'
export const REGISTRATION_L2O_PATH = '/registration/l2o'
export const REGISTRATION_INACTIVE_PATH = '/registration/inactive/'
export const CONFIRM_INVITATION_PATH = '/confirm-invitation/'
export const CONFIRM_FAILED_PATH = '/confirm-failed/'
export const MOBILE_AUTH_PATH = '/mobile-auth/'
export const SEARCH_ASYNC_PATH = '/search/'
export const SEARCH_ASYNC_RESULTS_PATH = '/search/results/'
export const SAVED_LOADS_PATH = '/saved-loads/'
export const CONFIRM_INFO_PATH = '/confirm-info/'
export const SIGN_UP_DRIVER_PATH = '/signup/driver/'
export const TEAM_PATH = '/team/'
export const USERS_PROFILE_PATH = '/users/:id'
export const PROFILE_PATH = '/profile/'
export const COMPANY_PATH = '/company/'
export const COMPANY_SETUP_PATH = '/company/setup/'
export const COMPANY_EQUIPMENT_PATH = '/company/equipment/'
export const COMPANY_FINANCE_PATH = '/company/finance/'
export const COMPANY_DRIVERS_PATH = '/company/drivers/'
export const COMPANY_DRIVER_DETAIL_PATH = '/company/drivers/:onboardingId/'
export const DRIVER_ASSIGNMENTS_PATH = '/driver-onboarding/:onboardingId?/'
export const DRIVER_SCREENING_CONSENT_PATH = '/driver-screening-consent/:onboardingId/'
export const FLEET_PROFILE_PATH = '/profile/fleet/'
export const FLEET_MEMBERS_PROFILE_PATH = '/profile/fleet-members/'
export const EQUIPMENT_PROFILE_PATH = '/profile/equipment/'
export const EQUIPMENT_PROFILE_PATH_ADD_TRUCK = '/profile/equipment/add-truck/'
export const EQUIPMENT_PROFILE_PATH_EDIT_TRUCK = '/profile/equipment/edit-truck/:equipmentId'
export const EQUIPMENT_PROFILE_PATH_ADD_TRAILER = '/profile/equipment/add-trailer/'
export const EQUIPMENT_PROFILE_PATH_EDIT_TRAILER = '/profile/equipment/edit-trailer/:equipmentId'
export const TRUCK_EQUIPMENT_PATH = '/profile/equipment/truck/:equipmentId'
export const TRAILER_EQUIPMENT_PATH = '/profile/equipment/trailer/:equipmentId'
export const PROFILE_SUBSCRIPTION_PATH = '/profile/subscription/'
export const CANCEL_SUBSCRIPTION_PATH = '/subscription/cancel/'
export const ISSUES_PATH = '/issues/'
export const BROKERS_PROFILE_PATH = '/profile/brokers/'
export const PAYMENT_INFORMATION_PROFILE_PATH = '/profile/payment-information/'
export const PAYMENT_INFORMATION_BANK_ACCOUNT_PROFILE_PATH = '/profile/payment-information/bank-account/'
export const PAYMENT_INFORMATION_BANK_ACCOUNT_ASSETS_PROFILE_PATH = '/profile/payment-information/bank-account/assets/'
export const PAYMENT_INFORMATION_CONFIRM_CT_CASH_PROFILE_PATH = '/profile/payment-information/confirm-ct-cash/'
export const CDL_UPLOAD_PATH = '/cdl-upload/:userId/'
export const JOBS_PATH = '/jobs/'
export const JOBS_PAST_PATH = '/jobs/past/'
export const CONTACTED_LOADS_PATH = '/contacted-loads/'
export const JOBS_TEMPLATES_PATH = '/jobs/templates/'
export const JOBS_TEMPLATES_NEW_PATH = '/jobs/templates/new/'
export const JOBS_TEMPLATES_EDIT_PATH = '/jobs/templates/:id/edit/'
export const JOBS_DETAILS_PATH = '/jobs/details/:id/'
export const JOBS_COPY_PATH = '/jobs/copy/:id/'
export const JOBS_TRACKING_PATH = '/jobs/tracking/:id/'
export const JOBS_DETAILS_EDIT_PATH = '/jobs/details/:id/edit/'
export const JOBS_DETAILS_CREATE_INVOICE_PATH = '/jobs/details/:id/create-invoice/'
export const JOBS_DETAILS_NOTIFY_BROKER_PATH = '/jobs/details/:id/notify-broker/:invoice'
export const JOBS_DETAILS_NOTIFY_FACTORING_COMPANY_PATH = '/jobs/details/:id/notify-factoring-company/'
export const EXTERNAL_BOOK_PATH = '/external-book/'
export const EXTERNAL_BOOK_FROM_TEMPLATE_PATH = '/external-book/:template/'
export const LOADS_DETAILS_PATH = '/loads/details/:id/'
export const LOADS_DETAILS_BOOK_PATH = '/loads/details/:id/book/:type'
export const LOADS_BIDDING_REQUEST_SUCCESS_PATH = '/loads/details/:id/bidding-submitted/'
export const EDIT_CURRENT_USER_ROLE_PATH = '/user-role/'
export const EDIT_USER_ROLE_PATH = '/user-role/:id'
export const ADD_USER_PATH = '/add-user/'
export const ADD_ADMIN_PATH = '/add-admin/'
export const ADD_DRIVER_PATH = '/add-driver/'
export const ADD_ADMIN_DRIVER_PATH = '/add-admin-driver/'
export const PERMISSIONS_SETTINGS_PATH = '/permissions-settings/'
export const DEACTIVATE_USER_PATH = '/deactivate-user/:id'
export const ONBOARDING_TASK_LIST_PATH = '/onboarding/tasklist/'
export const ONBOARDING_USER_TASK_LIST_PATH = '/onboarding/tasklist/:userId/'
export const ONBOARDING_ADD_USER_PATH = '/onboarding/add-user/'
export const ONBOARDING_ADD_DRIVER_PATH = '/onboarding/add-driver/'
export const ONBOARDING_ADD_ADMIN_PATH = '/onboarding/add-admin/'
export const ONBOARDING_ADD_ADMIN_DRIVER_PATH = '/onboarding/add-admin-driver/'
export const ONBOARDING_FLEET_APPLICATION_PATH = '/onboarding/fleet-application/'
export const ONBOARDING_FLEET_DETAILS_PATH = '/onboarding/fleet-details/'
export const ONBOARDING_TAX_INFO_PATH = '/onboarding/tax-info/'
export const ONBOARDING_APPLICATION_STATUS_PATH = '/onboarding/application-status/'
export const ONBOARDING_DRUG_SCREEN_PATH = '/onboarding/drug-screen/'
export const ONBOARDING_CLEARINGHOUSE_PATH = '/onboarding/clearinghouse/'
export const ONBOARDING_ELD_SETUP_PATH = '/onboarding/eld-setup/'
export const ONBOARDING_EQUIPMENT_ADD_TRUCK_PATH = '/onboarding/equipment-setup/add-truck/'
export const ONBOARDING_EQUIPMENT_EDIT_TRUCK_PATH = '/onboarding/equipment-setup/edit-truck/:equipmentId'
export const ONBOARDING_EQUIPMENT_ADD_TRAILER_PATH = '/onboarding/equipment-setup/add-trailer/'
export const ONBOARDING_EQUIPMENT_EDIT_TRAILER_PATH = '/onboarding/equipment-setup/edit-trailer/:equipmentId'
export const ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_PLATES = '/onboarding/equipment-setup/truck-documents/:equipmentId/plates/'
export const ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_DOT_INSPECTION = '/onboarding/equipment-setup/truck-documents/:equipmentId/dot-inspection/'
export const ONBOARDING_BROKER_SETUP_PATH = '/onboarding/broker-setup/'
export const ONBOARDING_BANK_ACCOUNT_PATH = '/onboarding/bank-account/'
export const ONBOARDING_VERIFY_STRIPE_PROFILE_PATH = '/onboarding/verify-stripe-profile/'
export const ONBOARDING_CONFIRM_CT_CASH_PATH = '/onboarding/confirm-ct-cash/'
export const ONBOARDING_SIGN_LEASE_AGREEMENT_PATH = '/onboarding/sign-lease-agreement/'
export const ONBOARDING_SIGN_LEASE_AGREEMENT_AMENDMENT_PATH = '/onboarding/sign-lease-agreement-amendment/'
export const ONBOARDING_DRIVER_APPLICATION_PATH = '/onboarding/driver-application/'
export const ONBOARDING_EMPLOYMENT_HISTORY = EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY
export const ONBOARDING_EMPLOYMENT_HISTORY_ADD = EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY_ADD
export const ONBOARDING_EMPLOYMENT_HISTORY_EDIT = EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY_EDIT
export const ONBOARDING_INSURANCE_SETUP_PATH = '/onboarding/insurance-setup/'
export const ONBOARDING_INSURANCE_BENEFICIARY_PATH = '/onboarding/insurance-beneficiary/'
export const ONBOARDING_INSURANCE_DOCUMENTS = '/onboarding/insurance-documents/'
export const ONBOARDING_MAINTENANCE_SET_ASIDE = '/onboarding/maintenance-fund/'
export const ONBOARDING_PSP_CONSENT = '/onboarding/psp-consent/'
export const ONBOARDING_DAC_CONSENT = '/onboarding/dac-consent/'

export const ONBOARDING_PICK_UP_PAY_AMENDMENT = '/onboarding/pick-up-pay-amendment/'
export const ONBOARDING_TRUCK_LEASE_APPLICATION = '/onboarding/truck-lease-application/'

export const ONBOARDING_WELCOME_PACKAGE_PATH = '/onboarding/welcome-package/'
export const ONBOARDING_NEXT_TASK_PATH = '/onboarding/next-task/'
export const ONBOARDING_TRUCK_DECALS_PATH = '/onboarding/truck-decals/'
export const CREDIT_PATH = '/ct-cash/credit/'

export const ACCOUNT_ACTIVITY_PATH = '/ct-cash/account-activity/'
export const CARDHOLDER_ACTIVITY = '/ct-cash/account-activity/:id/'
export const CARD_MANAGEMENT = '/ct-cash/card-management/'
export const CARD_MANAGEMENT_ADD_CARDHOLDER = '/ct-cash/card-management/add-cardholder/'
export const CARD_MANAGEMENT_CHANGE_WEEKLY_LIMIT = '/ct-cash/card-management/change-weekly-limit/:id/'
export const PLAID_REDIRECT = '/plaid-redirect/'
export const REVENUE_CALCULATOR = '/revenue-calculator/'
export const MARKET_CONDITIONS_PATH = '/market-conditions/'
export const SAFETY_DASHBOARD_PATH = '/safety/'
export const CONTRACTOR_PAYMENTS_PATH = '/contractor-payments/'
export const CONTRACTOR_PAYMENTS_PAYROLL_BANK_ACCOUNT_PATH = '/contractor-payments/payroll-bank-account/'
export const CONTRACTOR_PAYMENTS_ADD_CONTRACTORS_PATH = '/contractor-payments/add-contractors/'
export const CONTRACTOR_PERSONAL_PERSONAL_INFO_PATH = '/contractor-payments/personal-info/'
export const CONTRACTOR_PERSONAL_DIRECT_DEPOSIT_PATH = '/contractor-payments/direct-deposit/'
export const CONTRACTOR_PERSONAL_DEBIT_CARD_PATH = '/contractor-payments/debit-card/'
export const CONTRACTOR_PAYMENTS_CONTRACTORS_PATH = '/contractor-payments/contractors/'
export const CONTRACTOR_PAYMENTS_PERSONAL_SETTINGS_PATH = '/contractor-payments/settings/personal/'
export const CONTRACTOR_PAYMENTS_PAY_PATH = '/contractor-payments/pay/'
export const CONTRACTOR_PAYMENTS_USER_PAY_PATH = '/contractor-payments/pay/:userId'
export const CONTRACTOR_PAYMENTS_INSTANT_PAYOUTS_ONBOARDING_PATH = '/contractor-payments/instant-payouts-onboarding/'
export const CONTRACTOR_PAYMENTS_VERIFY_PROFILE_PATH = '/onboarding/contractor-payments/verify-profile/'
export const REPORT_PATH = '/profile/fleet/report'
export const VENDOR_DEDUCTIONS_PATH = '/vendor/deductions'
export const VENDOR_DEDUCTION_DETAIL_PATH = '/vendor/deductions/:id'
export const VENDOR_DEDUCTION_REFINANCE_PATH = '/vendor/deductions/:id/refinance/'
export const VENDOR_DRIVERS_PATH = '/vendor/drivers'
export const VENDOR_DRIVER_DETAIL_PATH = '/vendor/drivers/:id'
export const VENDOR_DRIVER_DETAIL_ASSIGN_TRUCK_PATH = '/vendor/drivers/:id/assign-truck/'
export const VENDOR_DRIVER_DETAIL_GENERATE_LEASE_PATH = '/vendor/drivers/:id/generate-lease/'
export const VENDOR_TRUCKS_PATH = '/vendor/trucks'
export const VENDOR_TRUCK_DETAIL_PATH = '/vendor/trucks/:id'
export const VENDOR_TRUCK_ADD_PATH = '/vendor/trucks/add'
export const VENDOR_TRUCK_IMPORT_PATH = '/vendor/trucks/import'
export const VENDOR_TRUCK_EDIT_PATH = '/vendor/trucks/edit/:id'

type RoutesType = Record<string, React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>>

const marketingRoutes: RoutesType = {
  [REVENUE_CALCULATOR]: RevenueCalculator,
}

const publicRoutes: RoutesType = {
  '/login': PasswordlessRedirect,
  [SIGN_IN_PATH]: SignInPage,
  [VERIFY_PATH]: VerifyCode,
  [REGISTRATION_PATH]: RegistrationPage,
  [REGISTRATION_L2O_PATH]: RegistrationPage,
  [REGISTRATION_INACTIVE_PATH]: DeadEndDeactivated,
  [CONFIRM_INVITATION_PATH]: ConfirmInvite,
  [CONFIRM_FAILED_PATH]: ConfirmFailedPage,
  [MOBILE_AUTH_PATH]: MobileAuth,
  [SELF_SERVE_BROKER_LOAD_TRACKING_LOOKUP_PATH]: SelfServeBrokerLoadTrackingLookupPage,
  [SELF_SERVE_BROKER_LOAD_TRACKING_PATH]: SelfServeBrokerLoadTrackingPage,
}

const approvedRoutes: RoutesType = {
  [SEARCH_ASYNC_PATH]: SearchFormAsync,
  [SEARCH_ASYNC_RESULTS_PATH]: SearchResultsFormAsync,
  [SAVED_LOADS_PATH]: BookmarkedLoads,
  [CONTACTED_LOADS_PATH]: ContactedLoads,
  [JOBS_DETAILS_PATH]: JobDetails,
  [JOBS_TRACKING_PATH]: JobTracking,
  [JOBS_DETAILS_EDIT_PATH]: JobDetailsEdit,
  [JOBS_DETAILS_CREATE_INVOICE_PATH]: JobCreateInvoice,
  [JOBS_DETAILS_NOTIFY_BROKER_PATH]: JobSendInvoiceToBroker,
  [JOBS_DETAILS_NOTIFY_FACTORING_COMPANY_PATH]: JobSendInvoiceToFactoringCompany,
  [JOBS_PATH]: JobsPage,
  [JOBS_COPY_PATH]: JobCopy,
  [JOBS_TEMPLATES_PATH]: JobsTemplates,
  [JOBS_TEMPLATES_NEW_PATH]: JobsNewTemplate,
  [JOBS_TEMPLATES_EDIT_PATH]: JobsEditTemplate,
  [JOBS_PAST_PATH]: JobsPastPage,
  [EXTERNAL_BOOK_PATH]: ExternalBook,
  [EXTERNAL_BOOK_FROM_TEMPLATE_PATH]: ExternalBook,
  [LOADS_DETAILS_PATH]: LoadDetails,
  [LOADS_DETAILS_BOOK_PATH]: LoadDetailsBook,
  [ACCOUNT_ACTIVITY_PATH]: AccountActivity,

  [CARDHOLDER_ACTIVITY]: AccountActivity,
  [CARD_MANAGEMENT]: CardManagement,
  [CARD_MANAGEMENT_ADD_CARDHOLDER]: AddCardHolder,
  [CARD_MANAGEMENT_CHANGE_WEEKLY_LIMIT]: ChangeWeeklyLimit,
  [CONTRACTOR_PAYMENTS_PATH]: ContractorPayments,
  [CONTRACTOR_PAYMENTS_PAYROLL_BANK_ACCOUNT_PATH]: ContractorPaymentsPayrollBankAccount,
  [CONTRACTOR_PAYMENTS_ADD_CONTRACTORS_PATH]: ContractorPaymentsAddContractors,
  [CONTRACTOR_PERSONAL_DIRECT_DEPOSIT_PATH]: ContractorPaymentsDirectDeposit,
  [CONTRACTOR_PERSONAL_DEBIT_CARD_PATH]: ContractorPaymentsDebitCard,
  [CONTRACTOR_PAYMENTS_CONTRACTORS_PATH]: ContractorPaymentsContractors,
  [CONTRACTOR_PAYMENTS_PAY_PATH]: ContractorPaymentsPay,
  [CONTRACTOR_PAYMENTS_USER_PAY_PATH]: ContractorPaymentsPay,
  [CONTRACTOR_PERSONAL_PERSONAL_INFO_PATH]: ContractorPaymentsPersonalInfo,
  [CONTRACTOR_PAYMENTS_PERSONAL_SETTINGS_PATH]: ContractorPaymentsPersonalSettings,
  [CONTRACTOR_PAYMENTS_INSTANT_PAYOUTS_ONBOARDING_PATH]: ContractorPaymentsInstantPayoutsOnboarding,
  [CONTRACTOR_PAYMENTS_VERIFY_PROFILE_PATH]: ContractorPaymentsVerifyProfile,
}

const privateRoutes: RoutesType = {
  [INDEX_PATH]: HomePage,
  [USERS_PROFILE_PATH]: ProfilePage,
  [PROFILE_PATH]: ProfilePage,
  [FLEET_PROFILE_PATH]: FleetPage,
  [FLEET_MEMBERS_PROFILE_PATH]: FleetMembersPage,
  [DRIVER_SCREENING_CONSENT_PATH]: DriverScreeningConsent,
  [DRIVER_ASSIGNMENTS_PATH]: DriverAssignments,
  [EQUIPMENT_PROFILE_PATH]: EquipmentPage,
  [EQUIPMENT_PROFILE_PATH_ADD_TRUCK]: EquipmentAddTruckPage,
  [EQUIPMENT_PROFILE_PATH_EDIT_TRUCK]: EquipmentEditTruckPage,
  [EQUIPMENT_PROFILE_PATH_ADD_TRAILER]: EquipmentAddTrailerPage,
  [EQUIPMENT_PROFILE_PATH_EDIT_TRAILER]: EquipmentEditTrailerPage,
  [TRUCK_EQUIPMENT_PATH]: EquipmentTruckPage,
  [TRAILER_EQUIPMENT_PATH]: EquipmentTrailerPage,
  [BROKERS_PROFILE_PATH]: BrokersPage,
  [PROFILE_SUBSCRIPTION_PATH]: SubscriptionPage,

  [PAYMENT_INFORMATION_PROFILE_PATH]: PaymentInformationPage,
  [PAYMENT_INFORMATION_BANK_ACCOUNT_PROFILE_PATH]: BankAccountPage,
  [PAYMENT_INFORMATION_BANK_ACCOUNT_ASSETS_PROFILE_PATH]: BankAccountAssetsPage,
  [PAYMENT_INFORMATION_CONFIRM_CT_CASH_PROFILE_PATH]: ConfirmCTCashPage,
  [CDL_UPLOAD_PATH]: UploadCDLPage,
  [EDIT_CURRENT_USER_ROLE_PATH]: EditUserRole,
  [EDIT_USER_ROLE_PATH]: EditUserRole,
  [ADD_USER_PATH]: AddUser,
  [ADD_ADMIN_PATH]: AddAdmin,
  [ADD_DRIVER_PATH]: AddDriver,
  [ADD_ADMIN_DRIVER_PATH]: AddAdminDriver,
  [PERMISSIONS_SETTINGS_PATH]: PermissionsSettings,
  [DEACTIVATE_USER_PATH]: DeactivateUser,
  [ONBOARDING_TASK_LIST_PATH]: OnboardingTaskListPage,
  [ONBOARDING_USER_TASK_LIST_PATH]: OnboardingTaskListPage,
  [ONBOARDING_ADD_DRIVER_PATH]: OnboardingAddDriver,
  [ONBOARDING_ADD_ADMIN_DRIVER_PATH]: OnboardingAddAdminDriver,
  [ONBOARDING_ADD_ADMIN_PATH]: OnboardingAddAdmin,
  [ONBOARDING_ADD_USER_PATH]: OnboardingAddUser,
  [ONBOARDING_FLEET_APPLICATION_PATH]: OnboardingFleetApplication,
  [ONBOARDING_FLEET_DETAILS_PATH]: OnboardingFleetDetails,
  [ONBOARDING_TAX_INFO_PATH]: OnboardingTaxInfo,
  [ONBOARDING_APPLICATION_STATUS_PATH]: OnboardingApplicationStatusPage,
  [ONBOARDING_DRUG_SCREEN_PATH]: OnboardingDrugScreenPage,
  [ONBOARDING_CLEARINGHOUSE_PATH]: OnboardingClearinghousePage,
  [ONBOARDING_ELD_SETUP_PATH]: OnboardingELDSetup,
  [ONBOARDING_EQUIPMENT_ADD_TRUCK_PATH]: OnboardingEquipmentAddTruck,
  [ONBOARDING_EQUIPMENT_EDIT_TRUCK_PATH]: OnboardingEquipmentEditTruck,
  [ONBOARDING_EQUIPMENT_ADD_TRAILER_PATH]: OnboardingEquipmentAddTrailer,
  [ONBOARDING_EQUIPMENT_EDIT_TRAILER_PATH]: OnboardingEquipmentEditTrailer,
  [ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_PLATES]: OnboardingEquipmentTruckDocumentsPage,
  [ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_DOT_INSPECTION]: OnboardingEquipmentTruckDocumentsPage,
  [ONBOARDING_BROKER_SETUP_PATH]: OnboardingBrokerSetup,
  [ONBOARDING_BANK_ACCOUNT_PATH]: OnboardingBankAccount,
  [ONBOARDING_VERIFY_STRIPE_PROFILE_PATH]: OnboardingStripeVerifyProfile,
  [ONBOARDING_CONFIRM_CT_CASH_PATH]: OnboardingConfirmCTCash,
  [ONBOARDING_SIGN_LEASE_AGREEMENT_PATH]: OnboardingSignLeaseAgreement,
  [ONBOARDING_SIGN_LEASE_AGREEMENT_AMENDMENT_PATH]: OnboardingSignLeaseAgreementAmendment,
  [ONBOARDING_DRIVER_APPLICATION_PATH]: OnboardingDriverApplication,
  [ONBOARDING_EMPLOYMENT_HISTORY]: OnboardingEmploymentHistory,
  [ONBOARDING_EMPLOYMENT_HISTORY_ADD]: OnboardingEmploymentHistoryAddPosition,
  [ONBOARDING_EMPLOYMENT_HISTORY_EDIT]: OnboardingEmploymentHistoryEditPosition,
  [ONBOARDING_INSURANCE_SETUP_PATH]: OnboardingInsuranceSetupPage,
  [ONBOARDING_INSURANCE_BENEFICIARY_PATH]: OnboardingInsuranceBeneficiaryPage,
  [ONBOARDING_INSURANCE_DOCUMENTS]: OnboardingInsuranceDocuments,
  [ONBOARDING_MAINTENANCE_SET_ASIDE]: OnboardingMaintenanceSetAside,
  [ONBOARDING_PSP_CONSENT]: OnboardingPspConsent,
  [ONBOARDING_DAC_CONSENT]: OnboardingDacConsent,

  [ONBOARDING_PICK_UP_PAY_AMENDMENT]: OnboardingPickUpPayAmendment,
  [ONBOARDING_TRUCK_LEASE_APPLICATION]: OnboardingTruckLeaseApplication,

  [ONBOARDING_WELCOME_PACKAGE_PATH]: OnboardingWelcomePackage,
  [ONBOARDING_TRUCK_DECALS_PATH]: OnboardingTruckDecals,
  [ONBOARDING_NEXT_TASK_PATH]: OnboardingNextTask,
  [PLAID_REDIRECT]: PlaidRedirect,
  [MARKET_CONDITIONS_PATH]: MarketConditions,
  [SAFETY_DASHBOARD_PATH]: SafetyDashboard,
  [CANCEL_SUBSCRIPTION_PATH]: CancelSubscription,
  [REPORT_PATH]: Report,
}

export const vendorDeductionsRoutes: RoutesType = {
  [VENDOR_DEDUCTIONS_PATH]: Deductions,
  [VENDOR_DEDUCTION_DETAIL_PATH]: DeductionDetail,
  [VENDOR_DEDUCTION_REFINANCE_PATH]: DeductionRefinance,
}

export const vendorL2ORoutes: RoutesType = {
  [VENDOR_DRIVERS_PATH]: VendorDrivers,
  [VENDOR_DRIVER_DETAIL_PATH]: VendorDriverDetail,
  [VENDOR_DRIVER_DETAIL_ASSIGN_TRUCK_PATH]: VendorDriverDetailAssignTruck,
  [VENDOR_DRIVER_DETAIL_GENERATE_LEASE_PATH]: VendorDriverDetailGenerateLease,
  [VENDOR_TRUCKS_PATH]: VendorTrucks,
  [VENDOR_TRUCK_DETAIL_PATH]: VendorTruckDetail,
  [VENDOR_TRUCK_ADD_PATH]: VendorTruckAdd,
  [VENDOR_TRUCK_EDIT_PATH]: VendorTruckEdit,
  [VENDOR_TRUCK_IMPORT_PATH]: VendorTruckImport,
}

export const marketingRoutesPaths = Object.keys(marketingRoutes)
export const publicRoutesPaths = Object.keys(publicRoutes)
export const approvedRoutesPaths = Object.keys(approvedRoutes)
export const privateRoutesPaths = Object.keys(privateRoutes)
export const vendorDeductionsRoutesPaths = Object.keys(vendorDeductionsRoutes)
export const vendorDL2ORoutesPaths = Object.keys(vendorL2ORoutes)

export const Routes = memo(() => {
  return (
    <Switch>
      <CompatRoute path="/manage" component={Management} />
      <CompatRoute path="/company" render={({ location }: RouteComponentProps) => <Redirect to={{ ...location, pathname: `/manage${location.pathname}` }} />} />
      <CompatRoute exact path={['/login', '/forgot-password', '/reset-password']} component={PasswordlessRedirect} />
      <PrivateRoute exact path="/issues/" component={FleetIssues} />
      <PrivateRoute exact path="/inbox/" component={FleetInbox} />
      <PrivateRoute exact path="/chats/" component={ChatsPage} />
      <PrivateRoute exact path="/issues/inbox/" component={InboxRedirect} />
      <PrivateRoute exact path="/my-performance/" component={MyPerformance} />
      <PrivateRoute exact path="/fleet/members/" component={FleetMembers} />
      <PrivateRoute exact path="/fleet/equipment/" component={FleetEquipment} />
      <CompatRoute exact path={SIGN_IN_PATH} component={publicRoutes[SIGN_IN_PATH]} />
      <CompatRoute path={VERIFY_PATH} component={publicRoutes[VERIFY_PATH]} />
      <Redirect exact from="/registration/flex-starter" to="/registration/premium" />
      <Redirect exact from="/registration/starter" to="/registration/premium" />
      <Redirect exact from="/registration/cash" to="/registration/premium" />
      <Redirect exact from="/registration/credit" to="/registration/premium" />
      <CompatRoute exact path="/registration/inactive" component={publicRoutes[REGISTRATION_INACTIVE_PATH]} />
      <CompatRoute exact path="/registration/l2o" component={publicRoutes[REGISTRATION_L2O_PATH]} />
      <CompatRoute exact path="/registration" component={publicRoutes[REGISTRATION_PATH]} />
      <CompatRoute path={REGISTRATION_PATH} component={publicRoutes[REGISTRATION_PATH]} />
      <Redirect exact from="/profile/payment-information/bank-accout/" to={PAYMENT_INFORMATION_BANK_ACCOUNT_PROFILE_PATH} />
      <CompatRoute path={CONFIRM_INVITATION_PATH} component={publicRoutes[CONFIRM_INVITATION_PATH]} />
      <CompatRoute path={CONFIRM_FAILED_PATH} component={publicRoutes[CONFIRM_FAILED_PATH]} />
      <CompatRoute path={MOBILE_AUTH_PATH} component={publicRoutes[MOBILE_AUTH_PATH]} />
      <CompatRoute exact path={SELF_SERVE_BROKER_LOAD_TRACKING_LOOKUP_PATH} component={publicRoutes[SELF_SERVE_BROKER_LOAD_TRACKING_LOOKUP_PATH]} />
      <CompatRoute exact path={SELF_SERVE_BROKER_LOAD_TRACKING_PATH} component={publicRoutes[SELF_SERVE_BROKER_LOAD_TRACKING_PATH]} />
      <CompatRoute path={REVENUE_CALCULATOR} component={marketingRoutes[REVENUE_CALCULATOR]} />
      <ApprovedRoute path={SEARCH_ASYNC_RESULTS_PATH} component={approvedRoutes[SEARCH_ASYNC_RESULTS_PATH]} />
      <ApprovedRoute path={SEARCH_ASYNC_PATH} component={approvedRoutes[SEARCH_ASYNC_PATH]} />
      <ApprovedRoute path={SAVED_LOADS_PATH} component={approvedRoutes[SAVED_LOADS_PATH]} />
      <ApprovedRoute path={CONTACTED_LOADS_PATH} component={approvedRoutes[CONTACTED_LOADS_PATH]} />
      <ApprovedRoute path="/external-book/inbox/:emailId/:fileId/" component={InboxRateconBook} />
      <ApprovedRoute path={EXTERNAL_BOOK_FROM_TEMPLATE_PATH} component={approvedRoutes[EXTERNAL_BOOK_FROM_TEMPLATE_PATH]} />
      <ApprovedRoute path={EXTERNAL_BOOK_PATH} component={approvedRoutes[EXTERNAL_BOOK_PATH]} />
      <ApprovedRoute path={JOBS_TEMPLATES_EDIT_PATH} component={approvedRoutes[JOBS_TEMPLATES_EDIT_PATH]} />
      <ApprovedRoute path={JOBS_TEMPLATES_NEW_PATH} component={approvedRoutes[JOBS_TEMPLATES_NEW_PATH]} />
      <ApprovedRoute path={JOBS_COPY_PATH} component={approvedRoutes[JOBS_COPY_PATH]} />
      <ApprovedRoute path={JOBS_DETAILS_CREATE_INVOICE_PATH} component={approvedRoutes[JOBS_DETAILS_CREATE_INVOICE_PATH]} />
      <ApprovedRoute path={JOBS_DETAILS_NOTIFY_BROKER_PATH} component={approvedRoutes[JOBS_DETAILS_NOTIFY_BROKER_PATH]} />
      <ApprovedRoute path={JOBS_DETAILS_NOTIFY_FACTORING_COMPANY_PATH} component={approvedRoutes[JOBS_DETAILS_NOTIFY_FACTORING_COMPANY_PATH]} />
      <ApprovedRoute path={JOBS_DETAILS_EDIT_PATH} component={approvedRoutes[JOBS_DETAILS_EDIT_PATH]} />
      <ApprovedRoute path={JOBS_DETAILS_PATH} component={approvedRoutes[JOBS_DETAILS_PATH]} />
      <ApprovedRoute path={JOBS_TRACKING_PATH} component={approvedRoutes[JOBS_TRACKING_PATH]} />
      <ApprovedRoute path={JOBS_PAST_PATH} component={approvedRoutes[JOBS_PAST_PATH]} />
      <ApprovedRoute path={JOBS_TEMPLATES_PATH} component={approvedRoutes[JOBS_TEMPLATES_PATH]} />
      <ApprovedRoute path={JOBS_PATH} component={approvedRoutes[JOBS_PATH]} />
      <ApprovedRoute path={LOADS_DETAILS_BOOK_PATH} component={approvedRoutes[LOADS_DETAILS_BOOK_PATH]} />
      <ApprovedRoute path={LOADS_DETAILS_PATH} component={approvedRoutes[LOADS_DETAILS_PATH]} />
      <Redirect exact from="/ct-cash/activity/" to={ACCOUNT_ACTIVITY_PATH} />
      <ApprovedRoute exact path={ACCOUNT_ACTIVITY_PATH} component={approvedRoutes[ACCOUNT_ACTIVITY_PATH]} />
      <ApprovedRoute exact path={CARDHOLDER_ACTIVITY} component={approvedRoutes[CARDHOLDER_ACTIVITY]} />
      <ApprovedRoute exact path={CARD_MANAGEMENT} component={approvedRoutes[CARD_MANAGEMENT]} />
      <ApprovedRoute exact path={CARD_MANAGEMENT_ADD_CARDHOLDER} component={approvedRoutes[CARD_MANAGEMENT_ADD_CARDHOLDER]} />
      <ApprovedRoute exact path={CARD_MANAGEMENT_CHANGE_WEEKLY_LIMIT} component={approvedRoutes[CARD_MANAGEMENT_CHANGE_WEEKLY_LIMIT]} />
      <Redirect exact path={CREDIT_PATH} to={ACCOUNT_ACTIVITY_PATH} />
      <ApprovedRoute exact path={CONTRACTOR_PAYMENTS_PATH} component={approvedRoutes[CONTRACTOR_PAYMENTS_PATH]} />
      <ApprovedRoute exact path={CONTRACTOR_PAYMENTS_PAYROLL_BANK_ACCOUNT_PATH} component={approvedRoutes[CONTRACTOR_PAYMENTS_PAYROLL_BANK_ACCOUNT_PATH]} />
      <ApprovedRoute exact path={CONTRACTOR_PERSONAL_DIRECT_DEPOSIT_PATH} component={approvedRoutes[CONTRACTOR_PERSONAL_DIRECT_DEPOSIT_PATH]} />
      <ApprovedRoute exact path={CONTRACTOR_PERSONAL_PERSONAL_INFO_PATH} component={approvedRoutes[CONTRACTOR_PERSONAL_PERSONAL_INFO_PATH]} />
      <ApprovedRoute exact path={CONTRACTOR_PERSONAL_DEBIT_CARD_PATH} component={approvedRoutes[CONTRACTOR_PERSONAL_DEBIT_CARD_PATH]} />
      <ApprovedRoute exact path={CONTRACTOR_PAYMENTS_ADD_CONTRACTORS_PATH} component={approvedRoutes[CONTRACTOR_PAYMENTS_ADD_CONTRACTORS_PATH]} />
      <ApprovedRoute exact path={CONTRACTOR_PAYMENTS_CONTRACTORS_PATH} component={approvedRoutes[CONTRACTOR_PAYMENTS_CONTRACTORS_PATH]} />
      <ApprovedRoute exact path={CONTRACTOR_PAYMENTS_USER_PAY_PATH} component={approvedRoutes[CONTRACTOR_PAYMENTS_USER_PAY_PATH]} />
      <ApprovedRoute exact path={CONTRACTOR_PAYMENTS_PAY_PATH} component={approvedRoutes[CONTRACTOR_PAYMENTS_PAY_PATH]} />
      <ApprovedRoute exact path={CONTRACTOR_PAYMENTS_PERSONAL_SETTINGS_PATH} component={approvedRoutes[CONTRACTOR_PAYMENTS_PERSONAL_SETTINGS_PATH]} />
      <ApprovedRoute exact path={CONTRACTOR_PAYMENTS_INSTANT_PAYOUTS_ONBOARDING_PATH} component={approvedRoutes[CONTRACTOR_PAYMENTS_INSTANT_PAYOUTS_ONBOARDING_PATH]} />
      <ApprovedRoute exact path={CONTRACTOR_PAYMENTS_VERIFY_PROFILE_PATH} component={approvedRoutes[CONTRACTOR_PAYMENTS_VERIFY_PROFILE_PATH]} />
      <PrivateRoute exact path={DRIVER_ASSIGNMENTS_PATH} component={DriverAssignments} />
      <PrivateRoute exact path={DRIVER_SCREENING_CONSENT_PATH} component={privateRoutes[DRIVER_SCREENING_CONSENT_PATH]} />
      <PrivateRoute exact path={INDEX_PATH} component={privateRoutes[INDEX_PATH]} />
      <Redirect from={SIGN_UP_DRIVER_PATH} to={ADD_DRIVER_PATH} />
      <Redirect from={CONFIRM_INFO_PATH} to={ONBOARDING_TASK_LIST_PATH} />
      <PrivateRoute exact path={EDIT_USER_ROLE_PATH} component={privateRoutes[EDIT_USER_ROLE_PATH]} />
      <PrivateRoute exact path={EDIT_CURRENT_USER_ROLE_PATH} component={privateRoutes[EDIT_CURRENT_USER_ROLE_PATH]} />
      <PrivateRoute exact path={ADD_USER_PATH} component={privateRoutes[ADD_USER_PATH]} />
      <PrivateRoute exact path={ADD_ADMIN_PATH} component={privateRoutes[ADD_ADMIN_PATH]} />
      <PrivateRoute exact path={ADD_DRIVER_PATH} component={privateRoutes[ADD_DRIVER_PATH]} />
      <PrivateRoute exact path={ADD_ADMIN_DRIVER_PATH} component={privateRoutes[ADD_ADMIN_DRIVER_PATH]} />
      <PrivateRoute exact path={DEACTIVATE_USER_PATH} component={privateRoutes[DEACTIVATE_USER_PATH]} />
      <PrivateRoute exact path={PERMISSIONS_SETTINGS_PATH} component={privateRoutes[PERMISSIONS_SETTINGS_PATH]} />
      <Redirect from={TEAM_PATH} to={FLEET_PROFILE_PATH} />
      <PrivateRoute path={TEAM_PATH} component={privateRoutes[TEAM_PATH]} />
      <PrivateRoute path={USERS_PROFILE_PATH} component={privateRoutes[USERS_PROFILE_PATH]} />
      <PrivateRoute exact path={PROFILE_PATH} component={privateRoutes[USERS_PROFILE_PATH]} />
      <PrivateRoute exact path={FLEET_PROFILE_PATH} component={privateRoutes[FLEET_PROFILE_PATH]} />
      <PrivateRoute exact path={FLEET_MEMBERS_PROFILE_PATH} component={privateRoutes[FLEET_MEMBERS_PROFILE_PATH]} />
      <PrivateRoute exact path={EQUIPMENT_PROFILE_PATH} component={privateRoutes[EQUIPMENT_PROFILE_PATH]} />
      <PrivateRoute exact path={PROFILE_SUBSCRIPTION_PATH} component={privateRoutes[PROFILE_SUBSCRIPTION_PATH]} />
      <PrivateRoute exact path={EQUIPMENT_PROFILE_PATH_ADD_TRUCK} component={privateRoutes[EQUIPMENT_PROFILE_PATH_ADD_TRUCK]} />
      <PrivateRoute exact path={EQUIPMENT_PROFILE_PATH_EDIT_TRUCK} component={privateRoutes[EQUIPMENT_PROFILE_PATH_EDIT_TRUCK]} />
      <PrivateRoute exact path={EQUIPMENT_PROFILE_PATH_ADD_TRAILER} component={privateRoutes[EQUIPMENT_PROFILE_PATH_ADD_TRAILER]} />
      <PrivateRoute exact path={EQUIPMENT_PROFILE_PATH_EDIT_TRAILER} component={privateRoutes[EQUIPMENT_PROFILE_PATH_EDIT_TRAILER]} />
      <PrivateRoute exact path={TRUCK_EQUIPMENT_PATH} component={privateRoutes[TRUCK_EQUIPMENT_PATH]} />
      <PrivateRoute exact path={TRAILER_EQUIPMENT_PATH} component={privateRoutes[TRAILER_EQUIPMENT_PATH]} />
      <PrivateRoute exact path={BROKERS_PROFILE_PATH} component={privateRoutes[BROKERS_PROFILE_PATH]} />
      <PrivateRoute exact path={PAYMENT_INFORMATION_PROFILE_PATH} component={privateRoutes[PAYMENT_INFORMATION_PROFILE_PATH]} />
      <PrivateRoute exact path={PAYMENT_INFORMATION_BANK_ACCOUNT_PROFILE_PATH} component={privateRoutes[PAYMENT_INFORMATION_BANK_ACCOUNT_PROFILE_PATH]} />
      <PrivateRoute exact path={PAYMENT_INFORMATION_BANK_ACCOUNT_ASSETS_PROFILE_PATH} component={privateRoutes[PAYMENT_INFORMATION_BANK_ACCOUNT_ASSETS_PROFILE_PATH]} />
      <PrivateRoute exact path={PAYMENT_INFORMATION_CONFIRM_CT_CASH_PROFILE_PATH} component={privateRoutes[PAYMENT_INFORMATION_CONFIRM_CT_CASH_PROFILE_PATH]} />
      <PrivateRoute path={CDL_UPLOAD_PATH} component={privateRoutes[CDL_UPLOAD_PATH]} />
      <PrivateRoute exact path={ONBOARDING_ADD_DRIVER_PATH} component={privateRoutes[ONBOARDING_ADD_DRIVER_PATH]} />
      <PrivateRoute exact path={ONBOARDING_ADD_ADMIN_DRIVER_PATH} component={privateRoutes[ONBOARDING_ADD_ADMIN_DRIVER_PATH]} />
      <PrivateRoute exact path={ONBOARDING_ADD_ADMIN_PATH} component={privateRoutes[ONBOARDING_ADD_ADMIN_PATH]} />
      <PrivateRoute exact path={ONBOARDING_ADD_USER_PATH} component={privateRoutes[ONBOARDING_ADD_USER_PATH]} />
      <PrivateRoute exact path={ONBOARDING_TASK_LIST_PATH} component={privateRoutes[ONBOARDING_TASK_LIST_PATH]} />
      <PrivateRoute exact path={ONBOARDING_USER_TASK_LIST_PATH} component={privateRoutes[ONBOARDING_USER_TASK_LIST_PATH]} />
      <PrivateRoute exact path={ONBOARDING_FLEET_APPLICATION_PATH} component={privateRoutes[ONBOARDING_FLEET_APPLICATION_PATH]} />
      <PrivateRoute exact path={ONBOARDING_FLEET_DETAILS_PATH} component={privateRoutes[ONBOARDING_FLEET_DETAILS_PATH]} />
      <PrivateRoute exact path={ONBOARDING_TAX_INFO_PATH} component={privateRoutes[ONBOARDING_TAX_INFO_PATH]} />
      <PrivateRoute exact path={ONBOARDING_APPLICATION_STATUS_PATH} component={privateRoutes[ONBOARDING_APPLICATION_STATUS_PATH]} />
      <PrivateRoute exact path={ONBOARDING_DRUG_SCREEN_PATH} component={privateRoutes[ONBOARDING_DRUG_SCREEN_PATH]} />
      <PrivateRoute exact path={ONBOARDING_CLEARINGHOUSE_PATH} component={privateRoutes[ONBOARDING_CLEARINGHOUSE_PATH]} />
      <PrivateRoute exact path={ONBOARDING_ELD_SETUP_PATH} component={privateRoutes[ONBOARDING_ELD_SETUP_PATH]} />
      <PrivateRoute exact path={ONBOARDING_EQUIPMENT_ADD_TRUCK_PATH} component={privateRoutes[ONBOARDING_EQUIPMENT_ADD_TRUCK_PATH]} />
      <PrivateRoute exact path={ONBOARDING_EQUIPMENT_EDIT_TRUCK_PATH} component={privateRoutes[ONBOARDING_EQUIPMENT_EDIT_TRUCK_PATH]} />
      <PrivateRoute exact path={ONBOARDING_EQUIPMENT_ADD_TRAILER_PATH} component={privateRoutes[ONBOARDING_EQUIPMENT_ADD_TRAILER_PATH]} />
      <PrivateRoute exact path={ONBOARDING_EQUIPMENT_EDIT_TRAILER_PATH} component={privateRoutes[ONBOARDING_EQUIPMENT_EDIT_TRAILER_PATH]} />
      <PrivateRoute exact path={ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_PLATES} component={privateRoutes[ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_PLATES]} />
      <PrivateRoute exact path={ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_DOT_INSPECTION} component={privateRoutes[ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_DOT_INSPECTION]} />
      <PrivateRoute exact path={ONBOARDING_BROKER_SETUP_PATH} component={privateRoutes[ONBOARDING_BROKER_SETUP_PATH]} />
      <PrivateRoute exact path={ONBOARDING_BANK_ACCOUNT_PATH} component={privateRoutes[ONBOARDING_BANK_ACCOUNT_PATH]} />
      <PrivateRoute exact path={ONBOARDING_VERIFY_STRIPE_PROFILE_PATH} component={privateRoutes[ONBOARDING_VERIFY_STRIPE_PROFILE_PATH]} />
      <PrivateRoute exact path={ONBOARDING_CONFIRM_CT_CASH_PATH} component={privateRoutes[ONBOARDING_CONFIRM_CT_CASH_PATH]} />
      <PrivateRoute exact path={ONBOARDING_SIGN_LEASE_AGREEMENT_PATH} component={privateRoutes[ONBOARDING_SIGN_LEASE_AGREEMENT_PATH]} />
      <PrivateRoute exact path={ONBOARDING_SIGN_LEASE_AGREEMENT_AMENDMENT_PATH} component={privateRoutes[ONBOARDING_SIGN_LEASE_AGREEMENT_AMENDMENT_PATH]} />
      <PrivateRoute exact path={ONBOARDING_DRIVER_APPLICATION_PATH} component={privateRoutes[ONBOARDING_DRIVER_APPLICATION_PATH]} />
      <PrivateRoute exact path={ONBOARDING_EMPLOYMENT_HISTORY} component={privateRoutes[ONBOARDING_EMPLOYMENT_HISTORY]} />
      <PrivateRoute exact path={ONBOARDING_EMPLOYMENT_HISTORY_ADD} component={privateRoutes[ONBOARDING_EMPLOYMENT_HISTORY_ADD]} />
      <PrivateRoute exact path={ONBOARDING_EMPLOYMENT_HISTORY_EDIT} component={privateRoutes[ONBOARDING_EMPLOYMENT_HISTORY_EDIT]} />
      <PrivateRoute exact path={ONBOARDING_INSURANCE_SETUP_PATH} component={privateRoutes[ONBOARDING_INSURANCE_SETUP_PATH]} />
      <PrivateRoute exact path={ONBOARDING_INSURANCE_BENEFICIARY_PATH} component={privateRoutes[ONBOARDING_INSURANCE_BENEFICIARY_PATH]} />
      <PrivateRoute exact path={ONBOARDING_INSURANCE_DOCUMENTS} component={privateRoutes[ONBOARDING_INSURANCE_DOCUMENTS]} />
      <PrivateRoute exact path={ONBOARDING_MAINTENANCE_SET_ASIDE} component={privateRoutes[ONBOARDING_MAINTENANCE_SET_ASIDE]} />
      <PrivateRoute exact path={ONBOARDING_PSP_CONSENT} component={privateRoutes[ONBOARDING_PSP_CONSENT]} />
      <PrivateRoute exact path={ONBOARDING_DAC_CONSENT} component={privateRoutes[ONBOARDING_DAC_CONSENT]} />
      <PrivateRoute exact path={ONBOARDING_PICK_UP_PAY_AMENDMENT} component={privateRoutes[ONBOARDING_PICK_UP_PAY_AMENDMENT]} />
      <PrivateRoute exact path={ONBOARDING_TRUCK_LEASE_APPLICATION} component={privateRoutes[ONBOARDING_TRUCK_LEASE_APPLICATION]} />
      <PrivateRoute exact path={ONBOARDING_WELCOME_PACKAGE_PATH} component={privateRoutes[ONBOARDING_WELCOME_PACKAGE_PATH]} />
      <PrivateRoute exact path={ONBOARDING_TRUCK_DECALS_PATH} component={privateRoutes[ONBOARDING_TRUCK_DECALS_PATH]} />
      <PrivateRoute exact path={ONBOARDING_NEXT_TASK_PATH} component={privateRoutes[ONBOARDING_NEXT_TASK_PATH]} />
      <PrivateRoute path={PLAID_REDIRECT} component={privateRoutes[PLAID_REDIRECT]} />
      <PrivateRoute path={MARKET_CONDITIONS_PATH} component={privateRoutes[MARKET_CONDITIONS_PATH]} />
      <PrivateRoute exact path={SAFETY_DASHBOARD_PATH} component={privateRoutes[SAFETY_DASHBOARD_PATH]} />
      <PrivateRoute exact path={CANCEL_SUBSCRIPTION_PATH} component={privateRoutes[CANCEL_SUBSCRIPTION_PATH]} />
      <PrivateRoute exact path={REPORT_PATH} component={privateRoutes[REPORT_PATH]} />
      <PrivateRoute exact path={VENDOR_DEDUCTIONS_PATH} component={vendorDeductionsRoutes[VENDOR_DEDUCTIONS_PATH]} />
      <PrivateRoute exact path={VENDOR_DEDUCTION_DETAIL_PATH} component={vendorDeductionsRoutes[VENDOR_DEDUCTION_DETAIL_PATH]} />
      <PrivateRoute exact path={VENDOR_DEDUCTION_REFINANCE_PATH} component={vendorDeductionsRoutes[VENDOR_DEDUCTION_REFINANCE_PATH]} />+
      <PrivateRoute exact path={VENDOR_DRIVERS_PATH} component={vendorL2ORoutes[VENDOR_DRIVERS_PATH]} />
      <PrivateRoute exact path={VENDOR_DRIVER_DETAIL_PATH} component={vendorL2ORoutes[VENDOR_DRIVER_DETAIL_PATH]} />
      <PrivateRoute exact path={VENDOR_DRIVER_DETAIL_ASSIGN_TRUCK_PATH} component={vendorL2ORoutes[VENDOR_DRIVER_DETAIL_ASSIGN_TRUCK_PATH]} />
      <PrivateRoute exact path={VENDOR_DRIVER_DETAIL_GENERATE_LEASE_PATH} component={vendorL2ORoutes[VENDOR_DRIVER_DETAIL_GENERATE_LEASE_PATH]} />
      <PrivateRoute exact path={VENDOR_TRUCKS_PATH} component={vendorL2ORoutes[VENDOR_TRUCKS_PATH]} />
      <PrivateRoute exact path={VENDOR_TRUCK_ADD_PATH} component={vendorL2ORoutes[VENDOR_TRUCK_ADD_PATH]} />
      <PrivateRoute exact path={VENDOR_TRUCK_IMPORT_PATH} component={vendorL2ORoutes[VENDOR_TRUCK_IMPORT_PATH]} />
      <PrivateRoute exact path={VENDOR_TRUCK_EDIT_PATH} component={vendorL2ORoutes[VENDOR_TRUCK_EDIT_PATH]} />
      <PrivateRoute exact path={VENDOR_TRUCK_DETAIL_PATH} component={vendorL2ORoutes[VENDOR_TRUCK_DETAIL_PATH]} />
      <CompatRoute component={PageNotFound} />
      <Redirect from="*" to={SIGN_IN_PATH} />
    </Switch>
  )
})
